import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Bold, Italic, Underline, List, ListOrdered, 
  AlignLeft, AlignCenter, AlignRight, Link, Heading1, Heading2, Quote,
  Code, Image, PanelLeft, Undo, Redo, LineChart
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SimpleRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  direction?: "ltr" | "rtl";
}

const SimpleRichTextEditor: React.FC<SimpleRichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Start writing...",
  direction = "ltr"
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const { toast } = useToast();

  // Initialize content
  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML.trim() && value) {
      editorRef.current.innerHTML = value;
    }
  }, []);

  // Apply text direction to editor content
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.dir = direction;
      // Force a re-render to ensure text direction is applied correctly
      const content = editorRef.current.innerHTML;
      editorRef.current.innerHTML = content;
    }
  }, [direction]);

  // Improve cursor position handling
  const saveSelection = () => {
    if (window.getSelection) {
      const sel = window.getSelection();
      if (sel && sel.getRangeAt && sel.rangeCount) {
        return sel.getRangeAt(0);
      }
    }
    return null;
  };

  const restoreSelection = (range: Range | null) => {
    if (range && window.getSelection) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  // Handle content changes
  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  };

  // Execute command on the document
  const execCommand = (command: string, value: string = '') => {
    const sel = saveSelection();
    
    try {
      document.execCommand(command, false, value);
      
      // Special check for list commands
      if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
        // Some browsers need this to properly render lists
        const content = editorRef.current?.innerHTML;
        if (editorRef.current && content) {
          // Add a small delay to ensure DOM is updated
          setTimeout(() => {
            // Force focus to remain in editor
            editorRef.current?.focus();
          }, 10);
        }
      }
    } catch (err) {
      console.error(`Error executing command ${command}:`, err);
      toast({
        title: "Editor Error",
        description: `Could not execute ${command}. Please try again.`,
        variant: "destructive",
      });
    }
    
    if (editorRef.current) {
      editorRef.current.focus();
      restoreSelection(sel);
      onChange(editorRef.current.innerHTML);
    }
  };

  // Handle list insertion with better browser compatibility
  const insertList = (type: 'ul' | 'ol') => {
    const sel = saveSelection();
    const command = type === 'ul' ? 'insertUnorderedList' : 'insertOrderedList';
    
    // First try the standard command
    const success = document.execCommand(command, false);
    
    // If standard command fails, use a fallback approach
    if (!success && editorRef.current) {
      const listTag = type === 'ul' ? 'ul' : 'ol';
      const selection = window.getSelection();
      
      if (selection && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        const fragment = range.extractContents();
        
        const list = document.createElement(listTag);
        const li = document.createElement('li');
        
        li.appendChild(fragment);
        list.appendChild(li);
        
        range.insertNode(list);
        
        // Restore selection at the end of the list
        selection.removeAllRanges();
        const newRange = document.createRange();
        newRange.selectNodeContents(li);
        newRange.collapse(false);
        selection.addRange(newRange);
      } else {
        // Insert an empty list
        const list = document.createElement(listTag);
        const li = document.createElement('li');
        
        li.innerHTML = '&nbsp;'; // Add a non-breaking space to ensure the list item has height
        list.appendChild(li);
        
        // Insert at current position
        document.execCommand('insertHTML', false, list.outerHTML);
        
        // Place cursor in the list item
        const allLists = editorRef.current.querySelectorAll(listTag);
        if (allLists.length > 0) {
          const lastList = allLists[allLists.length - 1];
          const items = lastList.querySelectorAll('li');
          if (items.length > 0) {
            const lastItem = items[items.length - 1];
            
            // Set cursor position
            selection?.removeAllRanges();
            const newRange = document.createRange();
            newRange.selectNodeContents(lastItem);
            newRange.collapse(false);
            selection?.addRange(newRange);
          }
        }
      }
      
      handleInput(); // Make sure changes are tracked
    }
    
    // Restore original selection if needed
    restoreSelection(sel);
  };

  // Handle link insertion
  const insertLink = () => {
    const selection = saveSelection();
    const url = prompt('Enter URL:');
    if (url) {
      restoreSelection(selection);
      execCommand('createLink', url);
    }
  };

  // Handle image insertion
  const insertImage = () => {
    const selection = saveSelection();
    const url = prompt('Enter image URL:');
    if (url) {
      restoreSelection(selection);
      execCommand('insertImage', url);
    }
  };

  // Handle paste to clean up HTML
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  // Force correct line breaks
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      // Force a proper line break
      e.preventDefault();
      document.execCommand('insertParagraph', false);
      // Scroll to cursor position
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const node = range.startContainer;
        if (node.nodeType === Node.TEXT_NODE && node.parentElement) {
          node.parentElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          (node as Element).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
  };

  // Get the appropriate alignment based on direction
  const getFlexDirection = () => {
    return direction === "rtl" ? "flex-row-reverse" : "flex";
  };

  return (
    <div className={`border rounded-md overflow-hidden ${isFocused ? 'ring-2 ring-primary' : ''}`}>
      {/* Toolbar */}
      <div className={`bg-muted p-2 border-b ${getFlexDirection()} flex-wrap gap-1`}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('undo')}
          title="Undo"
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('redo')}
          title="Redo"
        >
          <Redo className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('bold')}
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('italic')}
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('underline')}
          title="Underline"
        >
          <Underline className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('formatBlock', '<h1>')}
          title="Heading 1"
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('formatBlock', '<h2>')}
          title="Heading 2"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('formatBlock', '<blockquote>')}
          title="Quote"
        >
          <Quote className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('formatBlock', '<pre>')}
          title="Code Block"
        >
          <Code className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertList('ul')}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => insertList('ol')}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('justifyLeft')}
          title="Align Left"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('justifyCenter')}
          title="Align Center"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('justifyRight')}
          title="Align Right"
        >
          <AlignRight className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={insertLink}
          title="Insert Link"
        >
          <Link className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={insertImage}
          title="Insert Image"
        >
          <Image className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('insertHorizontalRule')}
          title="Insert Divider"
        >
          <PanelLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => execCommand('insertHTML', '<div class="chart-placeholder">Chart: Add chart here</div>')}
          title="Insert Chart Placeholder"
        >
          <LineChart className="h-4 w-4" />
        </Button>
      </div>

      {/* Editable content area */}
      <div
        ref={editorRef}
        contentEditable
        className={`min-h-[400px] p-4 focus:outline-none prose max-w-none whitespace-pre-wrap ${direction === "rtl" ? "text-right" : "text-left"}`}
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        dir={direction}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
      />

      <style>
        {`
          [contenteditable] {
            white-space: pre-wrap;
            word-wrap: break-word;
            -webkit-user-modify: read-write;
            overflow-wrap: break-word;
          }
          
          .prose {
            max-width: none;
          }
          
          .prose p {
            margin-bottom: 1em;
          }
          
          .prose h1 {
            font-size: 1.5em;
            font-weight: bold;
            margin-bottom: 0.5em;
            margin-top: 1em;
          }
          
          .prose h2 {
            font-size: 1.25em;
            font-weight: bold;
            margin-bottom: 0.5em;
            margin-top: 1em;
          }
          
          .prose blockquote {
            border-left: 4px solid #e5e7eb;
            padding-left: 1em;
            font-style: italic;
            margin: 1em 0;
          }
          
          .prose pre {
            background-color: #f3f4f6;
            padding: 1em;
            border-radius: 0.5em;
            font-family: monospace;
            overflow: auto;
            margin: 1em 0;
          }
          
          .prose ul, .prose ol {
            padding-left: 2em;
            margin: 1em 0;
          }
          
          .prose ul li {
            list-style-type: disc;
            margin-bottom: 0.5em;
          }
          
          .prose ol li {
            list-style-type: decimal;
            margin-bottom: 0.5em;
          }
          
          .prose img {
            max-width: 100%;
            height: auto;
            margin: 1em 0;
          }
          
          .chart-placeholder {
            background-color: #f3f4f6;
            border: 2px dashed #d1d5db;
            padding: 1.5em;
            text-align: center;
            margin: 1em 0;
            border-radius: 0.5em;
            color: #6b7280;
          }
          
          [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #9ca3af;
            pointer-events: none;
          }
          
          /* RTL specific styling */
          [dir="rtl"] {
            text-align: right;
          }
          
          [dir="rtl"] blockquote {
            border-left: none;
            border-right: 4px solid #e5e7eb;
            padding-left: 0;
            padding-right: 1em;
          }
          
          [dir="rtl"] ul, [dir="rtl"] ol {
            padding-left: 0;
            padding-right: 2em;
          }
          
          [dir="rtl"] .prose ul, [dir="rtl"] .prose ol {
            padding-left: 0;
            padding-right: 2em;
          }
        `}
      </style>
    </div>
  );
};

export default SimpleRichTextEditor;
